import React, { useState } from 'react';
import { Plus, X as XIcon } from 'lucide-react';
import { useT } from '../i18n.jsx';

// Picks the days an activity actually runs on.
//
// An activity used to hold one date, so a five-day trip had to be five separate activities -- and
// therefore five reply slips for one thing, which was the complaint that started this.
//
// The schedule is a PLAIN LIST OF DATES and nothing else. A recurrence rule ("every Monday and
// Wednesday from September to October") is offered only as a way to fill that list in; once filled,
// staff add and remove individual days. Storing the rule instead would mean recomputing it, and
// deciding what it means when a public holiday, an exam week or a typhoon falls inside it. A list
// has no such questions, and nobody issues a reply slip for a year of unbroken Mondays anyway.
// The numbers are JS day-of-week and are what the range fill compares; the label beside each is
// only how it is shown, and it comes from the locale rather than a dictionary -- a weekday name is
// not something a translator should be retyping. 2024-01-07 was a Sunday, so `7 + n` lands on the
// day `n` names.
const WEEKDAY_NUMBERS = [1, 2, 3, 4, 5, 6, 0];
const weekdayLabel = (locale, n) =>
  new Date(Date.UTC(2024, 0, 7 + n)).toLocaleDateString(locale, { weekday: 'short', timeZone: 'UTC' });

const iso = (d) => d.toISOString().slice(0, 10);

const SessionDatePicker = ({ dates = [], onChange, lockedDates = [] }) => {
  const { t, locale } = useT();
  const [single, setSingle] = useState('');
  const [rangeFrom, setRangeFrom] = useState('');
  const [rangeTo, setRangeTo] = useState('');
  const [weekdays, setWeekdays] = useState([]);

  const merge = (incoming) => {
    onChange([...new Set([...dates, ...incoming])].sort());
  };

  const addSingle = () => {
    if (!single) return;
    merge([single]);
    setSingle('');
  };

  // One helper for both "a continuous run" and "every Monday and Wednesday": with no weekday ticked
  // it fills every day in the range, and with some ticked it fills only those. Two buttons for what
  // is one operation would just be two places to fix.
  const addRange = () => {
    if (!rangeFrom || !rangeTo) return;
    const start = new Date(rangeFrom + 'T00:00:00');
    const end = new Date(rangeTo + 'T00:00:00');
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return;

    const out = [];
    // A guard, not a business rule: a mistyped year would otherwise try to build tens of thousands
    // of dates and hang the browser.
    for (let d = new Date(start), i = 0; d <= end && i < 400; d.setDate(d.getDate() + 1), i += 1) {
      if (weekdays.length === 0 || weekdays.includes(d.getDay())) out.push(iso(d));
    }
    merge(out);
  };

  const remove = (d) => {
    if (lockedDates.includes(d)) return;
    onChange(dates.filter((x) => x !== d));
  };

  const toggleWeekday = (n) =>
    setWeekdays((prev) => (prev.includes(n) ? prev.filter((x) => x !== n) : [...prev, n]));

  const field = { margin: 0, flex: '1 1 8rem', minWidth: '8rem' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>

      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ flex: '1 1 10rem' }}>
          <label className="input-label">{t('sessionDates.addADay')}</label>
          <input type="date" className="input-field" value={single}
                 onChange={(e) => setSingle(e.target.value)} style={field} />
        </div>
        <button type="button" className="btn-outline" onClick={addSingle}
                style={{ width: 'auto', height: '42px', padding: '0 0.9rem' }}>
          <Plus size={15} />
        </button>
      </div>

      <div style={{
        border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)',
        padding: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.6rem',
      }}>
        <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
          {t('sessionDates.fillRange')}
        </span>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <input type="date" className="input-field" value={rangeFrom}
                 onChange={(e) => setRangeFrom(e.target.value)} style={field} />
          <span style={{ alignSelf: 'center', color: 'var(--color-text-muted)' }}>{t('sessionDates.to')}</span>
          <input type="date" className="input-field" value={rangeTo}
                 onChange={(e) => setRangeTo(e.target.value)} style={field} />
        </div>
        <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
          {WEEKDAY_NUMBERS.map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => toggleWeekday(n)}
              style={{
                width: '2.1rem', height: '2.1rem', borderRadius: 'var(--radius-md)',
                cursor: 'pointer', fontSize: '0.85rem',
                border: '1px solid var(--color-border)',
                backgroundColor: weekdays.includes(n) ? 'var(--color-primary)' : 'transparent',
                color: weekdays.includes(n) ? '#fff' : 'var(--color-text-main)',
              }}
            >
              {weekdayLabel(locale, n)}
            </button>
          ))}
          <button type="button" className="btn-outline" onClick={addRange}
                  style={{ width: 'auto', padding: '0.55rem 0.9rem', marginLeft: 'auto' }}>
            {t('common.add')}
          </button>
        </div>
      </div>

      <div>
        <div style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', marginBottom: '0.4rem' }}>
          {[
            t('sessionDates.daysSelected', { count: dates.length }),
            ...(lockedDates.length > 0 ? [t('sessionDates.lockedNote', { count: lockedDates.length })] : []),
          ].join(' · ')}
        </div>
        {dates.length === 0 ? (
          <span style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>
            {t('sessionDates.noDays')}
          </span>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
            {dates.map((d) => {
              const locked = lockedDates.includes(d);
              return (
                <span
                  key={d}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
                    padding: '0.2rem 0.5rem', borderRadius: 'var(--radius-md)',
                    fontSize: '0.8rem',
                    backgroundColor: locked ? 'var(--color-bg-app)' : 'var(--color-primary-light)',
                    color: locked ? 'var(--color-text-muted)' : 'var(--color-text-main)',
                    border: '1px solid var(--color-border)',
                  }}
                >
                  {d}
                  {!locked && (
                    <button type="button" onClick={() => remove(d)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', color: 'inherit' }}>
                      <XIcon size={12} />
                    </button>
                  )}
                </span>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default SessionDatePicker;
